import { useEffect, useMemo, useState } from "react";
import { assignProjectToAccount } from "../../services/crmService";
import toast from "react-hot-toast";
import "./ProjectAccountAssignCard.css";

export default function ProjectAccountAssignCard({
    project,
    accounts,
    onAssigned
}) {
    const resolvedProjectId = project?.projectId || project?.id;
    const initialAccountId = project?.accountId || "";
    const [selectedAccountId, setSelectedAccountId] = useState(initialAccountId);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSelectedAccountId(project?.accountId || "");
    }, [project?.accountId]);

    const safeAccounts = useMemo(() => {
        return Array.isArray(accounts) ? accounts : [];
    }, [accounts]);

    const currentAccount = useMemo(() => {
        if (!project?.accountId) return null;

        return safeAccounts.find(
            account => String(account.id) === String(project.accountId)
        );
    }, [project?.accountId, safeAccounts]);

    const selectedAccount = useMemo(() => {
        if (!selectedAccountId) return null;

        return safeAccounts.find(
            account => String(account.id) === String(selectedAccountId)
        );
    }, [selectedAccountId, safeAccounts]);

    const isSameAccount =
        selectedAccountId &&
        project?.accountId &&
        String(selectedAccountId) === String(project.accountId);

    const handleAssign = async () => {
        if (!resolvedProjectId) {
            toast.error("Project id is missing.");
            return;
        }

        if (!selectedAccountId) {
            toast.error("Select an account first.");
            return;
        }

        if (isSameAccount) {
            toast("Project is already assigned to this account.");
            return;
        }

        try {
            setLoading(true);

            await assignProjectToAccount(resolvedProjectId, selectedAccountId);

            toast.success("Project assigned to account.");
            onAssigned?.();
        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data ||
                "Failed to assign project."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="snx-account-assign-card crm-card project-account-card">
            <div className="snx-account-assign-header project-account-head">
                <div>
                    <span className="snx-account-kicker">Super Admin</span>
                    <h3>{project?.businessName || project?.projectName || "Untitled Project"}</h3>
                    <p>
                        Assign this generated site to a client account for ownership,
                        CRM access, leads, and deployment control.
                    </p>
                </div>

                <div className="snx-account-current">
                    <span>Current owner</span>
                    <strong>
                        {currentAccount?.name ||
                            project?.accountName ||
                            project?.accountEmail ||
                            project?.accountId ||
                            "Unassigned"}
                    </strong>
                    {(currentAccount?.email || project?.accountEmail) && (
                        <small>{currentAccount?.email || project?.accountEmail}</small>
                    )}
                </div>
            </div>

            <div className="snx-account-meta-row">
                <div>
                    <span>Industry</span>
                    <strong>{project?.industry || "-"}</strong>
                </div>

                <div>
                    <span>Sub Industry</span>
                    <strong>{project?.subIndustry || "-"}</strong>
                </div>

                <div>
                    <span>Status</span>
                    <strong>{project?.status || "-"}</strong>
                </div>
            </div>

            {safeAccounts.length === 0 ? (
                <div className="snx-account-empty">
                    No client accounts found. Create a client account first, then assign this project.
                </div>
            ) : (
                <div className="snx-account-assign-row project-account-row">
                    <label>
                        <span>Assign to client account</span>
                        <select
                            className="crm-select"
                            value={selectedAccountId}
                            onChange={(e) => setSelectedAccountId(e.target.value)}
                        >
                            <option value="">Select account</option>
                            {safeAccounts.map((account) => (
                                <option key={account.id} value={account.id}>
                                    {account.name || account.email} ({account.email})
                                </option>
                            ))}
                        </select>
                    </label>

                    <button
                        type="button"
                        className="crm-btn crm-btn-primary"
                        onClick={handleAssign}
                        disabled={loading || !resolvedProjectId || !selectedAccountId || isSameAccount}
                    >
                        {loading
                            ? "Assigning..."
                            : isSameAccount
                                ? "Already Assigned"
                                : "Assign Project"}
                    </button>
                </div>
            )}

            {selectedAccount && !isSameAccount && (
                <div className="snx-account-preview">
                    <span>Selected account</span>
                    <strong>{selectedAccount.name || selectedAccount.email}</strong>
                    <small>{selectedAccount.email}</small>
                </div>
            )}
        </section>
    );
}